import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, UtensilsCrossed, Expand, Flower2 } from "lucide-react";
import { api } from "@/lib/api";
import { IMG } from "@/lib/images";
import { useLang } from "@/i18n/LanguageContext";
import Marquee from "@/components/Marquee";
import Reveal, { Caption } from "@/components/Reveal";
import Lightbox from "@/components/Lightbox";
import VillaEnquiryModal from "@/components/VillaEnquiryModal";
import ExcursionBookModal from "@/components/ExcursionBookModal";
import ExcursionCard from "@/components/ExcursionCard";
import VillaAvailability from "@/components/VillaAvailability";
import ReviewsSection from "@/components/ReviewsSection";
import FAQSection from "@/components/FAQSection";

const STAT_ICONS = ["bedrooms", "bathrooms", "guests", "sand"];

export default function HomePage() {
  const { t } = useLang();
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [booking, setBooking] = useState(null);
  const [excursions, setExcursions] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [posts, setPosts] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    api.get("/excursions").then((r) => setExcursions(r.data.slice(0, 3))).catch(() => {});
    api.get("/restaurants").then((r) => setRestaurants(r.data.slice(0, 4))).catch(() => {});
    api.get("/posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const heroLines = t("hero.lines");
  const heroStats = t("hero.stats");
  const amenities = t("villa.amenities");
  const galleryCaptions = t("gallery.captions");
  const galleryImages = [IMG.villaFront, IMG.villaSide, IMG.livingRoom, IMG.diningKitchen, IMG.diningRoom].map((src, i) => ({
    src,
    caption: Array.isArray(galleryCaptions) ? galleryCaptions[i] : "",
  }));

  return (
    <div>
      {/* ---------- HERO ---------- */}
      <section ref={heroRef} className="relative min-h-[100svh] flex flex-col overflow-hidden bg-night" data-testid="hero-section">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={IMG.hero} alt="Villa Paraiso front exterior in its quiet gated community" className="w-full h-[115%] object-cover object-[50%_70%]" />
          <div className="absolute inset-0 bg-gradient-to-b from-night/40 via-night/25 to-night/80" />
        </motion.div>

        <motion.div style={{ opacity: contentOpacity }} className="relative flex-1 flex flex-col justify-end max-w-7xl mx-auto w-full px-4 sm:px-8 lg:px-12 pb-14 pt-36">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-6"
          >
            {t("hero.caption")}
          </motion.p>
          <h1 data-testid="hero-heading" className="font-serif text-sand text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
            {heroLines.map((line, i) => (
              <span key={`${line}-${i}`} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.25 + i * 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 1 ? <span className="italic text-gold">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="text-sand/80 text-base sm:text-lg max-w-xl mt-6 leading-relaxed"
          >
            {t("hero.sub")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="flex flex-wrap gap-3 mt-8"
          >
            <button
              data-testid="hero-enquire-btn"
              onClick={() => setEnquiryOpen(true)}
              className="rounded-full bg-terracotta text-sand px-7 py-3.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors flex items-center gap-2"
            >
              {t("hero.enquire")} <ArrowRight size={16} />
            </button>
            <Link
              data-testid="hero-explore-btn"
              to="/excursions"
              className="rounded-full border border-sand/40 text-sand px-7 py-3.5 text-sm font-semibold hover:bg-sand hover:text-night transition-colors flex items-center gap-2"
            >
              {t("hero.explore")}
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="hidden sm:flex items-center gap-10 mt-14 text-sand/85"
          >
            {heroStats.map((s, i) => (
              <div key={s.label} className="flex items-center gap-3">
                <StatIcon kind={STAT_ICONS[i]} />
                <div>
                  <p className="font-serif text-2xl leading-none">{s.value}</p>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-sand/60 mt-1">{s.label}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <Marquee items={t("marquee.items")} testid="hero-marquee" />

      {/* ---------- THE VILLA ---------- */}
      <section id="villa" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-28" data-testid="villa-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5 flex flex-col justify-center">
            <Reveal>
              <Caption>{t("villa.caption")}</Caption>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mt-4 text-ink">
                {t("villa.title")}
              </h2>
              <p className="text-ink/60 leading-relaxed mt-6">{t("villa.copy")}</p>
              <div className="flex flex-wrap gap-2 mt-8">
                {amenities.map((a) => (
                  <span key={a} className="rounded-full border border-terracotta/25 bg-white px-4 py-1.5 text-xs font-medium text-ink/70">
                    {a}
                  </span>
                ))}
              </div>
              <button
                data-testid="villa-enquire-btn"
                onClick={() => setEnquiryOpen(true)}
                className="mt-10 rounded-full bg-night text-sand px-7 py-3.5 text-sm font-semibold hover:bg-terracotta transition-colors inline-flex items-center gap-2"
              >
                {t("villa.cta")} <ArrowUpRight size={16} />
              </button>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <div className="grid grid-cols-12 gap-3 sm:gap-4 auto-rows-[150px] sm:auto-rows-[190px]">
                {[
                  { src: IMG.villaFront, alt: "Villa Paraiso front exterior", cls: "col-span-12 sm:col-span-7 row-span-2" },
                  { src: IMG.villaSide, alt: "Villa exterior and lawn", cls: "col-span-6 sm:col-span-5" },
                  { src: IMG.livingRoom, alt: "Open living room", cls: "col-span-6 sm:col-span-5" },
                  { src: IMG.diningKitchen, alt: "Living room and kitchen", cls: "col-span-6 sm:col-span-5" },
                  { src: IMG.diningRoom, alt: "Dining room", cls: "col-span-6 sm:col-span-7" },
                ].map((img, i) => (
                  <button
                    key={img.src}
                    type="button"
                    data-testid={`gallery-photo-${i}`}
                    onClick={() => setLightboxIdx(i)}
                    aria-label={`${img.alt} — enlarge`}
                    className={`group relative ${img.cls} rounded-3xl overflow-hidden cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta`}
                  >
                    <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    <span className="absolute inset-0 bg-night/0 group-hover:bg-night/25 transition-colors duration-500" />
                    <span className="absolute bottom-3 right-3 p-2 rounded-full bg-night/60 text-sand opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <Expand size={14} />
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-ink/40 mt-3 font-mono uppercase tracking-[0.15em]" data-testid="gallery-hint">{t("gallery.hint")}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- AVAILABILITY & RATES ---------- */}
      <VillaAvailability onEnquire={() => setEnquiryOpen(true)} />

      {/* ---------- ISLAND SERVICES TEASER ---------- */}
      <section className="bg-night text-sand" data-testid="services-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-24">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <Caption>{t("services.teaserCaption")}</Caption>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mt-4 max-w-xl">
                {t("services.title")}
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="teaser-services-link"
              className="rounded-full border border-sand/25 px-6 py-3 text-sm font-semibold hover:bg-sand hover:text-night transition-colors inline-flex items-center gap-2"
            >
              {t("services.teaserCta")} <ArrowUpRight size={15} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { id: "chef", img: IMG.seafood, icon: <UtensilsCrossed size={18} /> },
              { id: "massage", img: IMG.gardenPath, icon: <Flower2 size={18} /> },
            ].map((s, i) => (
              <Reveal key={s.id} delay={0.1 * i}>
                <Link
                  to="/services"
                  data-testid={`teaser-service-${s.id}`}
                  className="group block rounded-3xl overflow-hidden border border-sand/10 hover:border-gold/40 transition-colors bg-white/5"
                >
                  <div className="relative h-52 sm:h-60 overflow-hidden">
                    <img src={s.img} alt={t(`services.${s.id}.name`)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    <span className="absolute top-4 left-4 rounded-full bg-night/70 backdrop-blur px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] text-sand/90">
                      {t("services.priceNote")}
                    </span>
                  </div>
                  <div className="p-6 sm:p-7">
                    <div className="flex items-center gap-2.5 text-gold mb-2">{s.icon}</div>
                    <h3 className="font-serif text-xl sm:text-2xl leading-tight">{t(`services.${s.id}.name`)}</h3>
                    <p className="text-sand/60 text-sm mt-2 leading-relaxed">{t(`services.${s.id}.tagline`)}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- EXPERIENCES ---------- */}
      <section className="bg-white border-y border-terracotta/10" data-testid="experiences-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-28">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <Caption>{t("homeExp.caption")}</Caption>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mt-4 text-ink max-w-xl">
                {t("homeExp.title")}
              </h2>
            </div>
            <Link data-testid="experiences-view-all" to="/excursions" className="text-sm font-semibold text-terracotta hover:text-terracotta-dark flex items-center gap-1.5 transition-colors">
              {t("homeExp.viewAll")} <ArrowRight size={15} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-excursions-grid">
            {excursions.map((e, i) => (
              <Reveal key={e.id} delay={i * 0.08}>
                <ExcursionCard excursion={e} onBook={setBooking} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- EAT & DRINK ---------- */}
      <section className="bg-teal text-sand" data-testid="dining-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-28">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <Caption light>{t("dining.caption")}</Caption>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mt-4 max-w-xl">
                {t("dining.title")}
              </h2>
            </div>
            <Link data-testid="dining-view-all" to="/restaurants" className="text-sm font-semibold text-gold hover:text-sand flex items-center gap-1.5 transition-colors">
              {t("dining.viewAll")} <ArrowRight size={15} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {restaurants.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.08}>
                <Link to="/restaurants" data-testid={`dining-card-${r.id}`} className="group block rounded-3xl overflow-hidden bg-teal-dark/60 border border-white/10 hover:border-gold/40 transition-colors">
                  <div className="h-44 overflow-hidden">
                    <img src={r.image} alt={r.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-gold/90">{r.cuisine} · {r.price_level}</p>
                    <h3 className="font-serif text-xl mt-1.5 leading-snug">{r.name}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- WHAT'S HOT ---------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-28" data-testid="blog-section">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <Caption>{t("homeBlog.caption")}</Caption>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mt-4 text-ink max-w-xl">
              {t("homeBlog.title")}
            </h2>
          </div>
          <Link data-testid="blog-view-all" to="/blog" className="text-sm font-semibold text-terracotta hover:text-terracotta-dark flex items-center gap-1.5 transition-colors">
            {t("homeBlog.viewAll")} <ArrowRight size={15} />
          </Link>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <Link to={`/blog/${p.slug}`} data-testid={`home-post-card-${p.slug}`} className="group block">
                <div className="rounded-3xl overflow-hidden h-60 mb-5">
                  <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-terracotta">{p.category}</p>
                <h3 className="font-serif text-2xl leading-snug mt-2 text-ink group-hover:text-terracotta transition-colors">{p.title}</h3>
                <p className="text-sm text-ink/55 mt-2 leading-relaxed line-clamp-2">{p.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <ReviewsSection />

      <FAQSection />

      <Marquee items={t("marquee.items2")} tone="night" slow testid="footer-marquee" />

      {/* ---------- CTA ---------- */}
      <section className="bg-night text-sand" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-24 sm:py-32 text-center">
          <Reveal>
            <div className="w-16 h-16 mx-auto rounded-full bg-gold/15 flex items-center justify-center mb-8">
              <MapPin className="text-gold" size={26} />
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl mx-auto">
              {t("cta.titleA")} <span className="italic text-gold">{t("cta.titleB")}</span>
            </h2>
            <p className="text-sand/60 max-w-lg mx-auto mt-6 leading-relaxed">{t("cta.sub")}</p>
            <div className="flex flex-wrap justify-center gap-3 mt-10">
              <button
                data-testid="cta-enquire-btn"
                onClick={() => setEnquiryOpen(true)}
                className="rounded-full bg-terracotta text-sand px-8 py-4 text-sm font-semibold hover:bg-terracotta-dark transition-colors inline-flex items-center gap-2"
              >
                {t("cta.enquire")} <ArrowRight size={16} />
              </button>
              <Link
                data-testid="cta-dine-btn"
                to="/restaurants"
                className="rounded-full border border-sand/30 text-sand px-8 py-4 text-sm font-semibold hover:bg-sand hover:text-night transition-colors inline-flex items-center gap-2"
              >
                <UtensilsCrossed size={16} /> {t("cta.dine")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <VillaEnquiryModal open={enquiryOpen} onClose={() => setEnquiryOpen(false)} />
      <ExcursionBookModal excursion={booking} open={!!booking} onClose={() => setBooking(null)} />
      <Lightbox images={galleryImages} index={lightboxIdx} onClose={() => setLightboxIdx(null)} onNavigate={setLightboxIdx} />
    </div>
  );
}

function StatIcon({ kind }) {
  const props = { size: 20, className: "text-gold" };
  if (kind === "bedrooms") return <BedIcon {...props} />;
  if (kind === "bathrooms") return <BathIcon {...props} />;
  if (kind === "guests") return <UsersIcon {...props} />;
  return <WavesIcon {...props} />;
}

import { BedDouble as BedIcon, Bath as BathIcon, Users as UsersIcon, Waves as WavesIcon } from "lucide-react";
